import { forwardRef } from 'react';
import clsx from 'clsx';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-body-sm font-medium text-text-primary mb-1.5"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={clsx(
            'block w-full rounded border bg-surface-primary px-3.5 py-2 text-body-sm text-text-primary placeholder-text-muted transition-colors',
            'focus:outline-none focus:ring-1',
            error
              ? 'border-error focus:border-error focus:ring-error'
              : 'border-border hover:border-border-dark focus:border-text-primary focus:ring-text-primary',
            className
          )}
          {...props}
        />
        {error && (
          <p className="mt-1.5 text-body-xs text-error">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-1.5 text-body-xs text-text-tertiary">{hint}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-body-sm font-medium text-text-primary mb-1.5"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          className={clsx(
            'block w-full rounded border bg-surface-primary px-3.5 py-2.5 text-body-sm text-text-primary placeholder-text-muted transition-colors resize-none',
            'focus:outline-none focus:ring-1',
            error
              ? 'border-error focus:border-error focus:ring-error'
              : 'border-border hover:border-border-dark focus:border-text-primary focus:ring-text-primary',
            className
          )}
          {...props}
        />
        {error && (
          <p className="mt-1.5 text-body-xs text-error">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-1.5 text-body-xs text-text-tertiary">{hint}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  /** Compact toolbar height (h-8) instead of the form height. */
  size?: 'sm' | 'md';
}

/** The one styled dropdown: a native select dressed like Input, so it works everywhere. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, size = 'md', className, id, children, ...props }, ref) => {
    const selectId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    const select = (
      <select
        ref={ref}
        id={selectId}
        className={clsx(
          'block appearance-none rounded border border-border bg-surface-primary bg-no-repeat pl-3 pr-8 text-body-sm text-text-primary transition-colors',
          'hover:border-border-dark focus:border-text-primary focus:outline-none focus:ring-1 focus:ring-text-primary disabled:opacity-40',
          "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236D6963' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")] bg-[length:12px] bg-[right_0.6rem_center]",
          size === 'sm' ? 'h-8' : 'h-9',
          className
        )}
        {...props}
      >
        {children}
      </select>
    );
    if (!label) return select;
    return (
      <div>
        <label htmlFor={selectId} className="mb-1.5 block text-body-sm font-medium text-text-primary">
          {label}
        </label>
        {select}
      </div>
    );
  }
);

Select.displayName = 'Select';
